import { api } from './client';
import type { Envelope } from './types';

export type MeetingStatus =
  | 'DRAFT'
  | 'VOTING'
  | 'CONFIRMED'
  | 'ONGOING'
  | 'SETTLING'
  | 'CLOSED';

export interface Meeting {
  id: string;
  title: string;
  region: string | null;
  memo: string | null;
  meetingPointName: string | null;
  meetingPointX: string | null;
  meetingPointY: string | null;
  status: MeetingStatus;
  hostUserId: string;
  confirmedDateCandidateId: string | null;
  confirmedDate: string | null;
  ongoingTriggeredBy: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MeetingListItem {
  id: string;
  title: string;
  region: string | null;
  status: MeetingStatus;
  hostUserId: string;
  participantCount: number;
  confirmedDate: string | null;
  updatedAt: string;
}

export async function listMeetings() {
  const { data } = await api.get<Envelope<MeetingListItem[]>>('/meetings');
  return data.data;
}

export interface MeetingPage {
  items: MeetingListItem[];
  nextCursor: string | null;
  limit: number;
}

export async function listMeetingsPage(cursor?: string | null, limit = 20): Promise<MeetingPage> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  const { data } = await api.get<{
    data: MeetingListItem[];
    meta?: { nextCursor?: string | null; limit?: number };
    traceId: string;
  }>(`/meetings?${params.toString()}`);
  return {
    items: data.data,
    nextCursor: data.meta?.nextCursor ?? null,
    limit: data.meta?.limit ?? limit,
  };
}

export async function getMeeting(id: string) {
  const { data } = await api.get<Envelope<Meeting>>(`/meetings/${id}`);
  return data.data;
}

export async function createMeeting(input: { title: string; region?: string; memo?: string }) {
  const { data } = await api.post<Envelope<Meeting>>('/meetings', input);
  return data.data;
}

/** 호스트 전용. 만나는 장소(집결지)는 이름+좌표 한 묶음으로 갱신된다. */
export async function updateMeetingPoint(
  id: string,
  point: { name: string; x: string; y: string },
) {
  const { data } = await api.patch<Envelope<Meeting>>(`/meetings/${id}`, {
    meetingPointName: point.name,
    meetingPointX: point.x,
    meetingPointY: point.y,
  });
  return data.data;
}

export async function cancelMeeting(id: string, reason?: string) {
  const { data } = await api.post<Envelope<Meeting>>(`/meetings/${id}/cancel`, { reason });
  return data.data;
}

export async function transitionMeeting(id: string, targetStatus: MeetingStatus) {
  const { data } = await api.post<Envelope<Meeting>>(`/meetings/${id}/transition`, { targetStatus });
  return data.data;
}

export interface ParticipantSummary {
  id: string;
  userId: string;
  nickname: string;
  role: string;
  joinedAt: string;
  leftAt: string | null;
}

export async function listParticipants(meetingId: string) {
  const { data } = await api.get<Envelope<ParticipantSummary[]>>(
    `/meetings/${meetingId}/participants`,
  );
  return data.data;
}
